import { type PaletteColor, useTheme } from "@mui/material/styles";
import type { TFunction } from "i18next";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ANNIE_STUDIO } from "@/theme/tokens";
import { AssistantCharacterAccessory, type AssistantCharacterAvatar } from "@/ui/AssistantAvatar/assistantAvatarConfig";
import { VisualChoiceShape } from "@/ui/Clickable/VisualChoiceGrid";
import { type AnnieStudioPartRow, AnnieStudioPartSections, partRow } from "./AnnieStudioPartSections";
import { bodyColorSwatch, pictureOptions, swatchOptions } from "./annieStudioOptions";
import { CHARACTER_SLOT_VALUES, type CharacterSlot } from "./annieStudioSlots";

type OnCharacterPartChange = <Slot extends CharacterSlot>(slot: Slot, value: AssistantCharacterAvatar[Slot]) => void;

type Props = {
	draft: AssistantCharacterAvatar;
	onChange: OnCharacterPartChange;
	disabled: boolean;
};

const characterRows = (
	t: TFunction,
	draft: AssistantCharacterAvatar,
	onChange: OnCharacterPartChange,
	disabled: boolean,
	primary: PaletteColor,
): AnnieStudioPartRow[] => {
	const bare = { ...draft, accessory: AssistantCharacterAccessory.None };
	const picture = <Slot extends CharacterSlot>(slot: Slot, titleKey: string, size: number, base: AssistantCharacterAvatar) =>
		partRow({
			slot,
			title: t(titleKey),
			value: draft[slot],
			options: pictureOptions(t, slot, CHARACTER_SLOT_VALUES[slot], (value) => ({ ...base, [slot]: value }), size),
			onChange: (value) => onChange(slot, value),
			shape: VisualChoiceShape.Rounded,
			disabled,
		});
	return [
		picture("shape", "annie.studio.slot.shape", ANNIE_STUDIO.PART_AVATAR, bare),
		partRow({
			slot: "color",
			title: t("annie.studio.slot.color"),
			value: draft.color,
			options: swatchOptions(t, "color", CHARACTER_SLOT_VALUES.color, (color) => bodyColorSwatch(color, primary)),
			onChange: (value) => onChange("color", value),
			shape: VisualChoiceShape.Circle,
			disabled,
		}),
		picture("eyes", "annie.studio.slot.eyes", ANNIE_STUDIO.FACE_AVATAR, bare),
		picture("accessory", "annie.studio.slot.extras", ANNIE_STUDIO.PART_AVATAR, draft),
	];
};

/** Shape, color, eyes and extras for the character style; every tile previews the draft with that one change. */
export const AnnieStudioCharacterParts: FC<Props> = ({ draft, onChange, disabled }) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const rows = useMemo(() => characterRows(t, draft, onChange, disabled, theme.palette.primary), [t, draft, onChange, disabled, theme.palette.primary]);
	return <AnnieStudioPartSections rows={rows} />;
};
